import {
    computed,
    inject,
    Injectable,
    signal,
    type Signal,
} from '@angular/core';
import { APP_CONFIG_TOKEN } from '../environment';
import {
    type FeatureFlag,
    FEATURE_FLAG,
    type FeatureFlagState,
    RUNTIME_FEATURE_FLAGS,
    type RuntimeFeatureFlag,
} from './models';

export const FEATURE_FLAG_OVERRIDE_STORAGE_KEY = 'qpx.featureFlagOverrides.v1';
const FEATURE_FLAG_OVERRIDE_STORAGE_VERSION = 1;

@Injectable({
    providedIn: 'root',
})
export class FeatureFlagOverrideService {
    //#region Dependency injection
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    //#endregion

    //#region State
    public readonly canOverrideFeatureFlags =
        !this.appConfig.useProductionUi &&
        isOverrideEnvironment(this.appConfig.env);
    private readonly canOverrideFeatureFlagsInDemo = signal(false);
    private readonly canMutateFeatureFlagOverrides = computed(
        () =>
            this.canOverrideFeatureFlags &&
            (this.appConfig.env !== 'demo' ||
                this.canOverrideFeatureFlagsInDemo())
    );
    private readonly defaultFeatureFlags = this.initializeFeatureFlags();
    private readonly overrides = signal<
        ReadonlyMap<RuntimeFeatureFlag, boolean>
    >(this.initializeOverrides());
    private readonly effectiveFeatureFlags = computed(() => {
        const overrides = this.overrides();
        const flags = new Map(this.defaultFeatureFlags);

        if (!this.canOverrideFeatureFlags) return flags;

        for (const [flag, value] of overrides) {
            flags.set(flag, value);
        }

        return flags;
    });
    public readonly flagStates = computed<readonly FeatureFlagState[]>(() => {
        const overrides = this.overrides();
        const effective = this.effectiveFeatureFlags();

        return RUNTIME_FEATURE_FLAGS.map((flag) => {
            const defaultValue = this.defaultFeatureFlags.get(flag) ?? false;
            const overrideValue = overrides.get(flag) ?? null;

            return {
                flag,
                defaultValue,
                overrideValue,
                effectiveValue: effective.get(flag) ?? defaultValue,
            };
        });
    });
    //#endregion

    //#region Public methods
    isFeatureEnabled(flagName: FeatureFlag, defaultValue = false): boolean {
        return this.effectiveFeatureFlags().get(flagName) ?? defaultValue;
    }

    setDemoOverrideAccess(isAllowed: boolean): void {
        this.canOverrideFeatureFlagsInDemo.set(isAllowed);
    }

    setOverride(flagName: FeatureFlag, value: boolean | null): void {
        if (!this.canMutateFeatureFlagOverrides()) return;
        if (!this.isRuntimeFeatureFlag(flagName)) return;

        if (value === null) {
            this.resetOverride(flagName);
            return;
        }

        const updatedOverrides = new Map(this.overrides());
        updatedOverrides.set(flagName, value);

        this.setOverrides(updatedOverrides);
    }

    resetOverride(flagName: FeatureFlag): void {
        if (!this.canMutateFeatureFlagOverrides()) return;
        if (!this.isRuntimeFeatureFlag(flagName)) return;

        const updatedOverrides = new Map(this.overrides());
        updatedOverrides.delete(flagName);

        this.setOverrides(updatedOverrides);
    }

    resetAllOverrides(): void {
        if (!this.canMutateFeatureFlagOverrides()) return;

        const overrides = new Map<RuntimeFeatureFlag, boolean>();
        this.setOverrides(overrides);
    }
    //#endregion

    //#region Private methods
    private initializeFeatureFlags(): ReadonlyMap<FeatureFlag, boolean> {
        const flags = this.appConfig.featureFlags;
        const featureFlags: Map<FeatureFlag, boolean> = new Map();

        for (const [key, _value] of Object.entries(flags)) {
            if (!this.isValidFeatureFlag(key)) {
                continue;
            }

            const value = typeof _value === 'boolean' ? _value : false;

            featureFlags.set(key, value);
        }

        return featureFlags;
    }

    private isValidFeatureFlag(key: string): key is FeatureFlag {
        return Object.values<string>(FEATURE_FLAG).includes(key);
    }

    private initializeOverrides(): ReadonlyMap<RuntimeFeatureFlag, boolean> {
        if (!this.canOverrideFeatureFlags) return new Map();

        const storage = getSessionStorage();
        if (!storage) return new Map();

        try {
            const storedValue = storage.getItem(
                FEATURE_FLAG_OVERRIDE_STORAGE_KEY
            );
            if (!storedValue) return new Map();

            const parsedValue: unknown = JSON.parse(storedValue);

            return this.parseStoredOverrides(parsedValue);
        } catch {
            return new Map();
        }
    }

    private parseStoredOverrides(
        value: unknown
    ): ReadonlyMap<RuntimeFeatureFlag, boolean> {
        if (!isRecord(value)) return new Map();
        if (value['version'] !== FEATURE_FLAG_OVERRIDE_STORAGE_VERSION) {
            return new Map();
        }
        if (!isRecord(value['overrides'])) return new Map();

        const overrides = new Map<RuntimeFeatureFlag, boolean>();

        for (const [key, overrideValue] of Object.entries(value['overrides'])) {
            if (!this.isRuntimeFeatureFlag(key)) continue;
            if (typeof overrideValue !== 'boolean') continue;

            overrides.set(key, overrideValue);
        }

        return overrides;
    }

    private isRuntimeFeatureFlag(key: string): key is RuntimeFeatureFlag {
        return RUNTIME_FEATURE_FLAGS.some((flag) => flag === key);
    }

    private setOverrides(
        overrides: ReadonlyMap<RuntimeFeatureFlag, boolean>
    ): void {
        this.writeStoredOverrides(overrides);
        this.overrides.set(overrides);
    }

    private writeStoredOverrides(
        overrides: ReadonlyMap<RuntimeFeatureFlag, boolean>
    ): void {
        const storage = getSessionStorage();
        if (!storage) return;

        try {
            if (overrides.size === 0) {
                clearFeatureFlagOverrideStorage();
                return;
            }

            storage.setItem(
                FEATURE_FLAG_OVERRIDE_STORAGE_KEY,
                JSON.stringify({
                    version: FEATURE_FLAG_OVERRIDE_STORAGE_VERSION,
                    overrides: Object.fromEntries(overrides),
                })
            );
        } catch {
            return;
        }
    }
    //#endregion
}

@Injectable({
    providedIn: 'root',
})
export class FeatureFlagService {
    //#region Dependency injection
    private readonly featureFlagOverrideService = inject(
        FeatureFlagOverrideService
    );
    //#endregion

    //#region State
    public readonly canOverrideFeatureFlags =
        this.featureFlagOverrideService.canOverrideFeatureFlags;
    //#endregion

    //#region Public methods
    /**
     * Checks if a specific feature flag is enabled
     * @param flagName - The name of the feature flag to check
     * @param defaultValue - Optional default value if flag is not found (defaults to false)
     * @returns boolean indicating if the feature is enabled
     */
    isFeatureEnabled(flagName: FeatureFlag, defaultValue = false): boolean {
        return this.featureFlagOverrideService.isFeatureEnabled(
            flagName,
            defaultValue
        );
    }

    flagEnabled(flagName: FeatureFlag, defaultValue = false): Signal<boolean> {
        return computed(() => this.isFeatureEnabled(flagName, defaultValue));
    }
    //#endregion
}

function isOverrideEnvironment(env: string): boolean {
    return (
        env === 'development' || env === 'qa' || env === 'uat' || env === 'demo'
    );
}

function getSessionStorage(): Storage | null {
    if (typeof window === 'undefined') return null;

    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

export function clearFeatureFlagOverrideStorage(): void {
    const storage = getSessionStorage();
    if (!storage) return;

    try {
        storage.removeItem(FEATURE_FLAG_OVERRIDE_STORAGE_KEY);
    } catch {
        return;
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
